import { createContext, memo, useContext, useEffect, useMemo, useState, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { AsyncState } from '@/components/async';
import { StatusBadge, type BadgeTone } from '@/components/dashboard/StatusBadge';
import { FilterModal } from '@/components/dashboard/filters/FilterModal';
import { UnitEditDrawer, UnitPublishToggle } from '@/components/dashboard/units';
import { PillButton } from '@/components/entity/PillButton';
import { useDashboardUser } from '@/routes/dashboard/DashboardRouter';
import { useDashboardPortfolioUnits } from '@/hooks/useDashboardUnits';
import { useDashboardUnitRow } from '@/hooks/useDashboardProperties';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import type {
  PortfolioUnitSummary,
  UnitAvailabilityStatus,
  UnitListFilters,
} from '@/lib/types/property';
import { PUBLIC_READINESS_LABELS } from '@/components/public/properties';
import {
  formatFloorLevelLabel,
  formatMoveInTimingLabel,
  formatSquareFeet,
  formatUnitLabel,
} from '@/lib/utils/propertyFormatting';

type UnitLayout = 'desktop' | 'tablet' | 'mobile';
type UnitToggleFilter = 'all' | 'enabled' | 'disabled';
type TableDensity = 'comfortable' | 'compact';

interface UnitFiltersState {
  readiness: UnitAvailabilityStatus[];
  displayOnPublicWebsite: UnitToggleFilter;
  outreachEmails: UnitToggleFilter;
}

interface DashboardUnitFiltersResult {
  filters: UnitFiltersState;
  appliedFilters: UnitListFilters;
  activeFilterCount: number;
  hasActiveFilters: boolean;
  resetFilters: () => void;
  updateFilters: (next: UnitFiltersState) => void;
}

type PortfolioUnitMap = Map<string, PortfolioUnitSummary>;

const PortfolioUnitContext = createContext<PortfolioUnitMap | null>(null);

function usePortfolioUnitSelector(unitId: string): PortfolioUnitSummary {
  const map = useContext(PortfolioUnitContext);
  if (!map) {
    throw new Error('usePortfolioUnitSelector must be used within PortfolioUnitContext');
  }
  const summary = map.get(unitId);
  if (!summary) {
    throw new Error(`Portfolio unit summary not found for id ${unitId}`);
  }
  return summary;
}

const DESKTOP_QUERY = '(min-width: 1280px)';
const TABLET_QUERY = '(min-width: 768px)';

/**
 * DashboardUnits renders a portfolio-wide unit listing for admins/managers with
 * filters, pagination, and unit-focused actions (publish toggle, edit, open
 * property manager). Layouts follow the property dashboard pattern across
 * desktop/tablet/mobile and reuse the single Supabase portfolio query.
 */
export function DashboardUnits(): JSX.Element {
  const user = useDashboardUser();
  const desktop = useMediaQuery(DESKTOP_QUERY);
  const tablet = useMediaQuery(TABLET_QUERY);
  const layout: UnitLayout = desktop ? 'desktop' : tablet ? 'tablet' : 'mobile';
  const navigate = useNavigate();

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [tableDensity, setTableDensity] = useState<TableDensity>(() => {
    if (typeof window === 'undefined') {
      return 'comfortable';
    }
    const stored = window.localStorage.getItem('dashboard_units_density');
    return stored === 'compact' ? 'compact' : 'comfortable';
  });
  const {
    filters,
    appliedFilters,
    hasActiveFilters,
    activeFilterCount,
    resetFilters,
    updateFilters,
  } = useDashboardUnitFilters();
  const [page, setPage] = useState(1);

  const listQuery = useDashboardPortfolioUnits({
    user,
    filters: appliedFilters,
    page,
    pageSize: 25,
  });

  const handleOpenDetail = (propertyId: string, unitId: string) => {
    navigate(`/dashboard/units/${propertyId}/${unitId}`);
  };

  useEffect(() => {
    if (!listQuery.data) {
      return;
    }
    const safeTotalPages = Math.max(listQuery.data.totalPages, 1);
    if (page > safeTotalPages) {
      setPage(safeTotalPages);
    }
  }, [listQuery.data, page]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    window.localStorage.setItem('dashboard_units_density', tableDensity);
  }, [tableDensity]);

  const metricCards =
    listQuery.data != null
      ? [
          {
            label: 'Total units (filtered)',
            value: listQuery.data.metrics.total.toLocaleString(),
          },
          {
            label: 'Realtor outreach enabled (filtered)',
            value: listQuery.data.metrics.published.toLocaleString(),
          },
          {
            label: 'Leaseable (filtered)',
            value: listQuery.data.metrics.leaseable.toLocaleString(),
          },
        ]
      : [];

  const activeFilterLabel = hasActiveFilters
    ? `${activeFilterCount.toString()} active filter${activeFilterCount === 1 ? '' : 's'}`
    : 'No active filters';

  return (
    <section className="flex flex-col gap-xl py-8">
      <header className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-col gap-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-brand-primary/70">
            Inventory portfolio
          </p>
          <h1 className="text-3xl font-semibold text-text-primary">Units</h1>
          <p className="text-sm text-text-primary/70">
            View every unit across the portfolio with move-in readiness, availability status, public
            website display controls, and realtor outreach controls.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <PillButton
            variant="secondary"
            onClick={() => {
              navigate('/dashboard/properties');
            }}
          >
            Back to properties
          </PillButton>
        </div>
      </header>

      {metricCards.length > 0 ? (
        <div className="grid gap-4 md:grid-cols-3">
          {metricCards.map((metric) => (
            <div
              key={metric.label}
              className="rounded-2xl border border-surface-subtle bg-white px-4 py-3 text-sm text-text-primary shadow-sm"
            >
              <p className="text-xs uppercase tracking-wide text-text-primary/60">{metric.label}</p>
              <p className="mt-1 text-2xl font-semibold">{metric.value}</p>
            </div>
          ))}
        </div>
      ) : null}

      <div className="dashboard-filter-bar z-30 flex flex-col gap-2 rounded-2xl border border-surface-subtle bg-white/90 px-4 py-3 shadow-sm backdrop-blur md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
            Filters
          </span>
          <span className="text-sm text-text-primary/80">{activeFilterLabel}</span>
        </div>
        <div className="flex flex-wrap items-center gap-2 md:gap-3">
          <PillButton
            variant="secondary"
            size="sm"
            disabled={!hasActiveFilters}
            onClick={resetFilters}
          >
            Reset
          </PillButton>
          <div className="flex items-center gap-1 rounded-full border border-surface-subtle bg-white p-1">
            <button
              type="button"
              aria-pressed={tableDensity === 'comfortable'}
              onClick={() => {
                setTableDensity('comfortable');
              }}
              className={clsx(
                'min-h-9 rounded-full px-3 text-[0.65rem] font-semibold uppercase tracking-wide transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary',
                tableDensity === 'comfortable'
                  ? 'bg-brand-primary text-white shadow-sm'
                  : 'text-text-primary/70 hover:bg-brand-primary/5',
              )}
            >
              Comfortable
            </button>
            <button
              type="button"
              aria-pressed={tableDensity === 'compact'}
              onClick={() => {
                setTableDensity('compact');
              }}
              className={clsx(
                'min-h-9 rounded-full px-3 text-[0.65rem] font-semibold uppercase tracking-wide transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary',
                tableDensity === 'compact'
                  ? 'bg-brand-primary text-white shadow-sm'
                  : 'text-text-primary/70 hover:bg-brand-primary/5',
              )}
            >
              Compact
            </button>
          </div>
          <PillButton
            size="sm"
            onClick={() => {
              setFiltersOpen(true);
            }}
          >
            Open filters
          </PillButton>
        </div>
      </div>

      <FilterModal
        title="Filter units"
        description="Filter by availability status, public website display, or realtor outreach emails."
        trigger={null}
        activeFilterLabel={activeFilterLabel}
        hasActiveFilters={hasActiveFilters}
        onReset={resetFilters}
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
      >
        <DashboardUnitFilters
          filters={filters}
          onFiltersChange={(next) => {
            updateFilters(next);
            setPage(1);
          }}
        />
      </FilterModal>

      <AsyncState
        data={listQuery.data ?? null}
        isLoading={(listQuery.isLoading || listQuery.isFetching) && !listQuery.data}
        error={listQuery.error}
        skeleton={<UnitsSkeleton layout={layout} />}
      >
        {(result) => {
          const hasResults = result.totalItems > 0;
          const start = hasResults ? (result.page - 1) * result.pageSize + 1 : 0;
          const end = hasResults ? Math.min(result.page * result.pageSize, result.totalItems) : 0;
          const safeTotalPages = Math.max(result.totalPages, 1);
          const unitMap: PortfolioUnitMap = new Map(result.items.map((unit) => [unit.id, unit]));
          const unitIds = result.items.map((unit) => unit.id);

          return (
            <PortfolioUnitContext.Provider value={unitMap}>
              <div className="flex flex-col gap-6">
                <UnitTable
                  unitIds={unitIds}
                  layout={layout}
                  density={tableDensity}
                  onOpenProperty={(propertyId) => {
                    navigate(`/dashboard/properties/${propertyId}/units`);
                  }}
                  onOpenDetail={handleOpenDetail}
                />
                <div className="flex flex-col gap-3 rounded-2xl border border-surface-subtle bg-white px-4 py-3 text-sm text-text-primary shadow-sm sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-sm text-text-primary/80">
                    {`Showing ${hasResults ? `${start.toLocaleString()}–${end.toLocaleString()}` : '0'} of ${result.totalItems.toLocaleString()} units`}
                  </p>
                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      className="rounded-full border border-surface-subtle px-4 py-2 text-sm font-semibold text-text-primary/80 transition hover:text-text-primary disabled:cursor-not-allowed disabled:opacity-50"
                      onClick={() => {
                        setPage((current) => Math.max(1, current - 1));
                      }}
                      disabled={result.page <= 1}
                    >
                      Previous
                    </button>
                    <span className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
                      Page {result.page} of {safeTotalPages}
                    </span>
                    <button
                      type="button"
                      className="rounded-full border border-surface-subtle px-4 py-2 text-sm font-semibold text-text-primary/80 transition hover:text-text-primary disabled:cursor-not-allowed disabled:opacity-50"
                      onClick={() => {
                        setPage((current) => Math.min(safeTotalPages, Math.max(1, current + 1)));
                      }}
                      disabled={result.page >= safeTotalPages}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            </PortfolioUnitContext.Provider>
          );
        }}
      </AsyncState>
    </section>
  );
}

function UnitTable({
  unitIds,
  layout,
  density,
  onOpenProperty,
  onOpenDetail,
}: {
  unitIds: string[];
  layout: UnitLayout;
  density: TableDensity;
  onOpenProperty: (propertyId: string) => void;
  onOpenDetail: (propertyId: string, unitId: string) => void;
}) {
  if (unitIds.length === 0) {
    return (
      <div className="bg-surface-muted/40 rounded-3xl border border-dashed border-surface-subtle p-8 text-center text-sm text-text-primary/70">
        No units match the current filters.
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-3xl border border-surface-subtle bg-white shadow-sm">
      <div className="overflow-x-auto">
        <table
          className="divide-surface-muted min-w-full divide-y text-sm text-text-primary"
          data-table-density={density}
        >
          <thead className="bg-surface-muted/40 text-xs uppercase tracking-wide text-text-primary/60">
            <tr>
              <th scope="col" className="px-4 py-3 text-left font-semibold">
                Unit
              </th>
              <th scope="col" className="px-4 py-3 text-left font-semibold">
                Property
              </th>
              <th scope="col" className="px-4 py-3 text-left font-semibold">
                Area
              </th>
              <th scope="col" className="px-4 py-3 text-left font-semibold">
                Rent
              </th>
              <th scope="col" className="px-4 py-3 text-left font-semibold">
                Move-in readiness
              </th>
              <th scope="col" className="px-4 py-3 text-left font-semibold">
                Availability status
              </th>
              <th scope="col" className="px-4 py-3 text-left font-semibold">
                Display on public website?
              </th>
              <th scope="col" className="px-4 py-3 text-left font-semibold">
                Realtor outreach emails
              </th>
              <th scope="col" className="px-4 py-3 text-left font-semibold">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-surface-muted/80 divide-y">
            {unitIds.map((unitId) => (
              <PortfolioUnitRow
                key={unitId}
                unitId={unitId}
                layout={layout}
                onOpenProperty={onOpenProperty}
                onOpenDetail={onOpenDetail}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PortfolioUnitRowComponent({
  unitId,
  layout,
  onOpenProperty,
  onOpenDetail,
}: {
  unitId: string;
  layout: UnitLayout;
  onOpenProperty: (propertyId: string) => void;
  onOpenDetail: (propertyId: string, unitId: string) => void;
}): JSX.Element {
  const user = useDashboardUser();
  const baseUnit = usePortfolioUnitSelector(unitId);
  const {
    data: rowData,
    isFetching,
    isLoading,
  } = useDashboardUnitRow({
    user,
    propertyId: baseUnit.propertyId,
    unit: baseUnit,
  });

  const summary: PortfolioUnitSummary = {
    ...(rowData ?? baseUnit),
    propertyLabel: baseUnit.propertyLabel,
    propertyCode: baseUnit.propertyCode,
  };

  const isRefreshing = isFetching && !isLoading;
  const areaLabel = formatSquareFeet(summary.areaSf);
  const floorLabel = formatFloorLevelLabel(summary.floorLevel, { emptyLabel: '' });
  const moveInReadinessLabel = formatMoveInReadinessLabel(summary);

  const handleRowClick = (event: React.MouseEvent<HTMLTableRowElement>) => {
    const target = event.target as HTMLElement | null;
    if (target?.closest('button, a, input, textarea, select')) {
      return;
    }
    onOpenDetail(summary.propertyId, summary.id);
  };

  return (
    <tr
      className="hover:bg-surface-muted/30 relative cursor-pointer align-top"
      onClick={handleRowClick}
      aria-busy={isRefreshing}
      data-row-refreshing={isRefreshing ? 'true' : 'false'}
    >
      <td className="px-4 py-4">
        {isRefreshing ? <RowLoadingOverlay /> : null}
        <div className="flex flex-col gap-1">
          <p className="text-sm font-semibold text-text-primary">{formatUnitLabel(summary)}</p>
          {floorLabel.length > 0 ? (
            <p className="text-xs text-text-primary/70">Floor {floorLabel}</p>
          ) : null}
        </div>
      </td>
      <td className="px-4 py-4">
        <div className="flex flex-col gap-1">
          {typeof summary.propertyLabel === 'string' && summary.propertyLabel.length > 0 ? (
            <p className="text-sm font-semibold text-text-primary">{summary.propertyLabel}</p>
          ) : typeof summary.propertyCode === 'string' && summary.propertyCode.length > 0 ? (
            <p className="text-sm font-semibold text-text-primary">{summary.propertyCode}</p>
          ) : null}
          {typeof summary.propertyCode === 'string' && summary.propertyCode.length > 0 ? (
            <p className="text-xs text-text-primary/60">Code {summary.propertyCode}</p>
          ) : null}
        </div>
      </td>
      <td className="px-4 py-4">{areaLabel ? `${areaLabel} SF` : ''}</td>
      <td className="px-4 py-4">{formatRent(summary)}</td>
      <td className="px-4 py-4">
        <span className="text-xs text-text-primary/70">{moveInReadinessLabel}</span>
      </td>
      <td className="px-4 py-4">
        <StatusBadge
          tone={readinessTone(summary.availability.status)}
          label={PUBLIC_READINESS_LABELS[summary.availability.status]}
        />
      </td>
      <td className="px-4 py-4">
        <StatusBadge
          tone={summary.displayOnPublicWebsite ? 'success' : 'neutral'}
          label={summary.displayOnPublicWebsite ? 'Yes' : 'No'}
        />
      </td>
      <td className="px-4 py-4">
        <StatusBadge
          tone={summary.published ? 'success' : 'neutral'}
          label={summary.published ? 'Yes' : 'No'}
        />
      </td>
      <td className="px-4 py-4">
        <div className="flex flex-wrap gap-2">
          <UnitPublishToggle propertyId={summary.propertyId} unit={summary} size="sm" />
          <UnitEditDrawer
            propertyId={summary.propertyId}
            unit={summary}
            layout={layout}
            trigger={<PillButton size="sm">Edit</PillButton>}
          />
          <PillButton
            size="sm"
            variant="secondary"
            onClick={() => {
              onOpenProperty(summary.propertyId);
            }}
          >
            Open property
          </PillButton>
        </div>
      </td>
    </tr>
  );
}

const PortfolioUnitRow = memo(PortfolioUnitRowComponent);

function RowLoadingOverlay(): JSX.Element {
  return (
    <div
      className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-white/80 backdrop-blur-sm"
      data-testid="row-loading-indicator"
      aria-label="Refreshing row"
    >
      <span className="h-5 w-5 animate-spin rounded-full border-2 border-brand-primary/40 border-t-brand-primary" />
    </div>
  );
}

function DashboardUnitFilters({
  filters,
  onFiltersChange,
}: {
  filters: UnitFiltersState;
  onFiltersChange: (next: UnitFiltersState) => void;
}): JSX.Element {
  const toggleReadiness = (status: UnitAvailabilityStatus, checked: boolean) => {
    onFiltersChange({
      ...filters,
      readiness: checked
        ? Array.from(new Set([...filters.readiness, status]))
        : filters.readiness.filter((entry) => entry !== status),
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <fieldset className="flex flex-col gap-2">
        <legend className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
          Availability status
        </legend>
        {READINESS_OPTIONS.map((status) => (
          <label key={status} className="flex items-center gap-2 text-sm text-text-primary/80">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-surface-subtle text-brand-primary focus:ring-brand-primary"
              checked={filters.readiness.includes(status)}
              onChange={(event) => {
                toggleReadiness(status, event.target.checked);
              }}
            />
            <span>{PUBLIC_READINESS_LABELS[status]}</span>
          </label>
        ))}
      </fieldset>

      <fieldset className="flex flex-col gap-2">
        <legend className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
          Display on public website?
        </legend>
        {TOGGLE_OPTIONS.map((option) => (
          <label
            key={option.value}
            className="flex items-center gap-2 text-sm text-text-primary/80"
          >
            <input
              type="radio"
              name="unit-display"
              className="h-4 w-4 border-surface-subtle text-brand-primary focus:ring-brand-primary"
              checked={filters.displayOnPublicWebsite === option.value}
              onChange={() => {
                onFiltersChange({
                  ...filters,
                  displayOnPublicWebsite: option.value,
                });
              }}
            />
            <span>{option.label}</span>
          </label>
        ))}
      </fieldset>

      <fieldset className="flex flex-col gap-2">
        <legend className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
          Realtor outreach emails
        </legend>
        {TOGGLE_OPTIONS.map((option) => (
          <label
            key={option.value}
            className="flex items-center gap-2 text-sm text-text-primary/80"
          >
            <input
              type="radio"
              name="unit-outreach"
              className="h-4 w-4 border-surface-subtle text-brand-primary focus:ring-brand-primary"
              checked={filters.outreachEmails === option.value}
              onChange={() => {
                onFiltersChange({
                  ...filters,
                  outreachEmails: option.value,
                });
              }}
            />
            <span>{option.label}</span>
          </label>
        ))}
      </fieldset>
    </div>
  );
}

function UnitsSkeleton({ layout }: { layout: UnitLayout }) {
  const rows = layout === 'desktop' ? 5 : 4;
  return (
    <div className="overflow-hidden rounded-3xl border border-surface-subtle bg-white shadow-sm">
      <div className="divide-surface-muted divide-y">
        {Array.from({ length: rows }).map((_item, index) => (
          <div key={index} className="bg-surface-muted/40 h-16 animate-pulse" />
        ))}
      </div>
    </div>
  );
}

const READINESS_OPTIONS: UnitAvailabilityStatus[] = ['available', 'unavailable', 'leased'];

const TOGGLE_OPTIONS: Array<{ value: UnitToggleFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'enabled', label: 'Yes only' },
  { value: 'disabled', label: 'No only' },
];

function formatMoveInReadinessLabel(unit: PortfolioUnitSummary): string {
  return formatMoveInTimingLabel(unit.availabilityStart, unit.possessionTimeline);
}

function readinessTone(status: UnitAvailabilityStatus): BadgeTone {
  switch (status) {
    case 'available':
      return 'success';
    case 'unavailable':
      return 'warning';
    default:
      return 'neutral';
  }
}

function formatRent(unit: PortfolioUnitSummary): string {
  const formatter = new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 2,
  });
  const suffix = 'SF Annual';
  const prefix = formatter.format(unit.rent.netRentPerSf);
  if (unit.rent.rentBasis === 'gross') {
    return `${prefix}/${suffix}`;
  }
  const addl =
    typeof unit.rent.additionalRentPerSf === 'number'
      ? ` + ${formatter.format(unit.rent.additionalRentPerSf)}/${suffix}`
      : '';
  return `${prefix}/${suffix}${addl}`;
}

function useDashboardUnitFilters(): DashboardUnitFiltersResult {
  const [filters, setFilters] = useState<UnitFiltersState>({
    readiness: [],
    displayOnPublicWebsite: 'all',
    outreachEmails: 'all',
  });

  const appliedFilters = useMemo<UnitListFilters>(() => {
    const readiness = filters.readiness.length > 0 ? filters.readiness : undefined;
    const displayOnPublicWebsite =
      filters.displayOnPublicWebsite === 'all'
        ? undefined
        : filters.displayOnPublicWebsite === 'enabled';
    const published =
      filters.outreachEmails === 'all' ? undefined : filters.outreachEmails === 'enabled';

    return {
      readiness,
      displayOnPublicWebsite,
      published,
    };
  }, [filters.displayOnPublicWebsite, filters.outreachEmails, filters.readiness]);

  const activeFilterCount =
    (appliedFilters.readiness?.length ?? 0) +
    (appliedFilters.displayOnPublicWebsite != null ? 1 : 0) +
    (appliedFilters.published != null ? 1 : 0);
  const hasActiveFilters = activeFilterCount > 0;

  const resetFilters = () => {
    setFilters({ readiness: [], displayOnPublicWebsite: 'all', outreachEmails: 'all' });
  };

  const updateFilters = (next: UnitFiltersState) => {
    setFilters(next);
  };

  return {
    filters,
    appliedFilters,
    activeFilterCount,
    hasActiveFilters,
    resetFilters,
    updateFilters,
  };
}
